import Image from 'next/image';
import IconTask from '../images/icon-sport.png';
import { Button } from '@/components/ui/button';
import { ReactNode, useCallback, useEffect } from 'react';
import StarTitle from './star-title';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { getGullakTaskList } from '../services';
import { useTranslations } from 'next-intl';
import type { GullakTaskRes } from '@/types/api/gullak-cash/gullak-task';
import { useBalanceFormatter } from '@/hooks/use-balance-formatter';
import { useAtom, useAtomValue } from 'jotai';
import { gullakTaskListAtom, gullakTaskTotalAtom, gullakTaskLoadingAtom, gullakHomeDataAtom, gullakBalanceAtom } from '@/store';
import { showWalletModal } from '@/features/wallet/components/wallet-modal';
import { showShareModal } from '@/features/referral/components/Share/modal';
import { useRouter } from '@/i18n/navigation';
import { menuTabUrl } from '@/const/page-url';
import { GullakTaskType, TaskFinishStatus, NumericalType, GULLAK_BIZ_TYPE_MAP_REPORT, ONCE_TASKS, BET_TASKS } from '../constants';
import { useGullakLogin } from '../hooks/use-gullak-login';
import { track } from '@/utils/track';
import style from './task-list.module.css';
import { showModalPhoneEdit } from '@/features/user-center/components/phone-edit';
import useGullakHomeData from '../hooks/use-gullak-home-data';
import { getCurrency } from '@/utils/currency';
import { toDP } from '@/libs/decimal';

type TaskItem = Omit<NonNullable<GullakTaskRes['tasks']>[0], 'task_type'> & { task_type: GullakTaskType; };

interface IProps {
  visible?: boolean;
  setHideModal: () => any;
}

/**
 * 将文本中的 {amount} 替换为高亮的金额
 * @param text 原始文本
 * @param amount 要替换的金额
 * @returns React 元素数组
 */
const replaceAmountWithHighlight = (text: string, amount: string): ReactNode[] => {
  const regex = /\{amount\}/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // 添加匹配前的文本
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.substring(lastIndex, match.index)}</span>);
    }
    // 添加高亮的金额
    parts.push(
      <span key={key++} className='text-[rgba(255,255,255,1)]'>
        {amount}
      </span>
    );
    lastIndex = regex.lastIndex;
  }

  // 添加剩余的文本
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.substring(lastIndex)}</span>);
  }

  return parts.length > 0 ? parts : [<span key={0}>{text}</span>];
};

/**
 * 格式化数值显示
 * @param numericalType 数值类型 1.金额，2比例
 * @param numerical 数值
 * @param currency 币种
 * @param formatCurrency 格式化货币的函数
 * @returns 格式化后的字符串
 */
const formatNumerical = (
  numericalType: string,
  numerical: string,
  currency: string,
  formatCurrency: (amount: number | string, currency: string) => string
): string => {
  if (numericalType === NumericalType.Amount) {
    // 金额：符号+数值
    return formatCurrency(numerical, currency);
  } else if (numericalType === NumericalType.Percentage) {
    // 比例：数值+%
    return `${numerical}%`;
  }
  return numerical;
};

const PAGE_SIZE = 20;

export default function TaskList({ visible = true, setHideModal }: IProps) {
  const t_common = useTranslations('common');
  const t_gullak = useTranslations('gullak');
  const { formatCurrency } = useBalanceFormatter();
  const router = useRouter();
  const { isLogin, handleShowLoginModal } = useGullakLogin();

  // 使用 atom 存储数据
  const [, setTaskList] = useAtom(gullakTaskListAtom);
  const [, setTaskTotal] = useAtom(gullakTaskTotalAtom);
  const [, setTaskLoading] = useAtom(gullakTaskLoadingAtom);

  // 读取首页接口返回的currency字段
  const homeData = useAtomValue(gullakHomeDataAtom);
  const currency = homeData?.currency || 'INR';

  // 读取 gullakBalance 用于监听 balance 变化
  const gullakBalance = useAtomValue(gullakBalanceAtom);

  const { updateGullakHomeData } = useGullakHomeData();

  /**
   * 处理任务按钮点击
   * @param taskType 任务类型 1-首充;2-充值;3-casino投注;4-sports报名;5-rummy参赛;6-邀请;7-提现
   */
  const handleTaskClick = (task: TaskItem) => {
    const { task_type: taskType } = task;
    if (isLogin) {
      track('gullak_page_button_click', {
        template_name: GULLAK_BIZ_TYPE_MAP_REPORT[taskType],
        task_id: task.task_id,
        is_finish: task.is_finished ? true : false
      });
    }
    switch (taskType) {
      case GullakTaskType.FirstDeposit: // 首充
      case GullakTaskType.Deposit: // 充值
      case GullakTaskType.Withdraw: // 提现
        // 未登录唤起登录弹窗，已登录唤起充值弹窗
        if (!isLogin) {
          handleShowLoginModal();
        } else {
          showWalletModal({
            entrance: 'root',
          });
        }
        break;
      case GullakTaskType.CasinoBet: // casino投注
        router.push(menuTabUrl.casino);
        setHideModal();
        break;
      case GullakTaskType.SportsJoin: // sports报名
        router.push(menuTabUrl.fantasy);
        setHideModal();
        break;
      case GullakTaskType.RummyJoin: // rummy参赛
        router.push(menuTabUrl.rummy);
        setHideModal();
        break;
      case GullakTaskType.Invite: // 邀请
        // 未登录唤起登录弹窗，已登录唤起分享弹窗
        if (!isLogin) {
          handleShowLoginModal();
        } else {
          setTimeout(() => {
            showShareModal();
          }, 300);
        }
        break;
      case GullakTaskType.BindPhone: // 绑定手机
        // 未登录唤起登录弹窗，已登录唤起分享弹窗
        if (!isLogin) {
          handleShowLoginModal();
        } else {
          showModalPhoneEdit({
            onSuccess: () => {
              // 更新任务列表数据
              resetAndFetch();
              // 更新
              updateGullakHomeData();
            }
          });
        }
        break;
      default:
        console.warn('Unknown task type:', taskType);
    }
  };

  const fetchTaskData = useCallback(
    async (page: number, pageSize: number) => {
      try {
        setTaskLoading(true);
        const result = await getGullakTaskList({
          page: page.toString(),
          page_size: pageSize.toString(),
        });

        const newTasks = result.tasks || [];
        const total = result.total || 0;

        // 更新 atom 数据
        if (page === 1) {
          setTaskList(newTasks);
        } else {
          setTaskList((prev) => [...prev, ...newTasks]);
        }
        setTaskTotal(total);

        return {
          list: newTasks as TaskItem[],
          total,
        };
      } catch (error) {
        console.error('Failed to fetch task list:', error);
        return {
          list: [],
          total: 0,
        };
      } finally {
        setTaskLoading(false);
      }
    },
    [setTaskList, setTaskTotal, setTaskLoading]
  );

  const {
    data: tasks,
    loading,
    scrollRef,
    resetAndFetch,
  } = useInfiniteScroll<TaskItem>({
    fetchData: fetchTaskData,
    pageSize: PAGE_SIZE,
    enabled: visible,
  });

  // 监听 visible 或 gullakBalance.balance 变化，刷新任务列表
  useEffect(() => {
    if (visible) {
      resetAndFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, gullakBalance?.balance]);

  const isInitLoading = loading && tasks.length === 0;
  const isEmpty = !loading && tasks.length === 0;
  const isLoadingMore = loading && tasks.length > 0;

  if (!visible || isInitLoading || isEmpty) {
    return null;
  }

  const renderProgress = (task: TaskItem) => {
    if (ONCE_TASKS.includes(task.task_type) || !Number(task.numerical_conditional)) {
      return null;
    }
    const prefix = BET_TASKS.includes(task.task_type) ? getCurrency(currency) : '';
    return (
      <>
        <span className='ml-2'>(</span>
        <span>{prefix}{toDP(Math.min(+task.numerical_finished, +task.numerical_conditional), 2)}&nbsp;</span>
        <span>/&nbsp;</span>
        <span className="text-secondary">{prefix}{toDP(Number(task.numerical_conditional), 2)}</span>
        <span>)</span>
      </>
    );
  };

  return (
    <div
      ref={scrollRef}
      className="mt-3 px-3 w-full flex flex-col items-center font-medium overflow-y-auto bg-[#181d29]"
    >
      <StarTitle text={t_gullak('getGullakCash')} />

      {(
        <>
          <div className='my-3 space-y-2 w-full'>
            {tasks.map((task) => {
              // 格式化数值显示
              const formattedNumerical = formatNumerical(
                task.numerical_type,
                task.numerical,
                currency,
                formatCurrency
              );

              return (
                <div
                  key={task.task_id}
                  className={`flex items-center justify-between pl-[11px] pr-[10px] pt-[15px] pb-[14px] rounded-lg ${style['task-card']}`}
                >
                  <div className='flex items-stretch'>
                    <div className='relative'>
                      <Image
                        src={task.unfinished_icon || IconTask.src}
                        width={58}
                        height={49}
                        alt='task'
                        priority
                        className='self-center'
                      />
                      {/* 已完成才显示 */}
                      {task.is_finished === TaskFinishStatus.Finished && (
                        <Image
                          src='/assets/images/icon-finished.png'
                          width={14}
                          height={14}
                          alt='finish'
                          priority
                          className='absolute bottom-2 right-2'
                        />
                      )}
                    </div>
                    <div className='flex-1 flex flex-col justify-center items-start ml-[6px] overflow-hidden'>
                      {/* 读取字段名external_title */}
                      <div className='font-medium text-[3.6vw] text-white leading-4'>
                        {task.external_title}
                        {renderProgress(task)}
                      </div>
                      <div className='font-medium mt-[6px] text-[3.1vw] line-clamp-2 text-tertiary leading-4'>
                        {/* 读取字段名subhead和numerical，还需根据字段名numerical_type去显示金额/比例，金额则符号+数值，比例则数值+% */}
                        {replaceAmountWithHighlight(task.subhead.replace('{amount_condition}', String(task.numerical_conditional || '')), formattedNumerical)}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant='primary'
                    className='ml-[17px] w-24 h-[9.4vw]'
                    onClick={() => handleTaskClick(task)}
                  >
                    {/* 1-首充;2-充值;3-casino投注;4-sports报名;5-rummy参赛;6-邀请;7-提现 */}
                    {task.btntxt}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* 加载更多指示器 */}
          <div className="w-full py-4 text-center">
            {isLoadingMore ? (
              <div className="flex items-center justify-center gap-2 text-tertiary text-xs">
                {t_common('loading')}
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
